import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useFeatureFlagWithContext from 'app/hooks/useFeatureFlagWithContext';
import { FEATURE_FLAGS } from 'app/constants/FeatureFlag';
import { useIndicationProgress } from '../useIndicationProgress';
import { trackMgmSidebarBannerLinkCopied } from '../trackMgmEvents';
import * as S from './styles';

type AuthState = {
	auth: {
		user?: {
			client_id?: string;
		};
	};
};

// Fallback matches REACT_APP_GAMIFICATION_INVITE_URL in .env; without it a
// missing env would build a broken link (`undefined?utm_source=...`).
const INVITE_URL_FALLBACK = 'https://page.jusfy.com.br/programa-de-indicacao';

const buildReferralLink = (clientId: string) =>
	`${process.env.REACT_APP_GAMIFICATION_INVITE_URL || INVITE_URL_FALLBACK}?utm_source=referral&utm_medium=mgm&utm_campaign=${clientId}`;

const formatIndicationCount = (count: number): string =>
	count === 1 ? '1 indicação confirmada' : `${count} indicações confirmadas`;

// Inner component so useIndicationProgress (a referrals GET) only runs when the
// flag is on — the outer component returns null before mounting this.
const MgmSidebarBannerContent: React.FC<{ clientId?: string }> = ({ clientId }) => {
	const [copied, setCopied] = useState(false);
	const referralLink = buildReferralLink(clientId || 'seu-codigo');
	const { count } = useIndicationProgress();

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(referralLink);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
			trackMgmSidebarBannerLinkCopied({ clientId });
		} catch (err) {
			console.error('Failed to copy:', err);
		}
	};

	return (
		<S.Container>
			<S.Content>
				<S.Title>
					Indique a Jusfy e ganhe 10 créditos para fazer suas consultas jurídicas
				</S.Title>

				<S.LinkRow>
					<S.LinkInput value={referralLink} readOnly aria-label="Link de indicação" />
					<S.CopyButton type="button" onClick={handleCopy}>
						<ContentCopyIcon sx={{ fontSize: 14 }} />
						{copied ? 'Link copiado' : 'Copiar link'}
					</S.CopyButton>
				</S.LinkRow>

				<S.Footer>
					<S.FooterText>+ 300 XP por advogado que assinar</S.FooterText>
					<S.FooterText>{formatIndicationCount(count)}</S.FooterText>
				</S.Footer>
			</S.Content>
		</S.Container>
	);
};

const MgmSidebarBanner: React.FC = () => {
	const user = useSelector((state: AuthState) => state.auth?.user);
	const { isFeatureFlagEnable: isMgmEnabled } = useFeatureFlagWithContext(
		FEATURE_FLAGS.RELEASE.MGM_ENABLED,
		{ clientId: user?.client_id ?? '' },
	);

	if (!isMgmEnabled) {
		return null;
	}

	return <MgmSidebarBannerContent clientId={user?.client_id} />;
};

export default MgmSidebarBanner;
